import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const DemoRequestForm: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    nome: '',
    email: '',
    empresa: '',
    perfil: '',
    mensagem: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.nome || !form.email || !form.empresa || !form.perfil) {
      toast({ title: 'Preencha todos os campos obrigatórios.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { error } = await (supabase as any)
        .from('landing_demo_requests')
        .insert({
          nome: form.nome.trim(),
          email: form.email.trim(),
          empresa: form.empresa.trim(),
          perfil: form.perfil,
          mensagem: form.mensagem.trim() || null,
        });
      if (error) throw error;
      setSuccess(true);
    } catch (err: any) {
      toast({ title: 'Erro ao enviar. Tente novamente.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-success" />
        </div>
        <h3 className="text-xl font-display font-bold text-foreground mb-2">Solicitação enviada!</h3>
        <p className="text-muted-foreground">Entraremos em contato em breve para agendar sua demonstração.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5 max-w-lg mx-auto">
      <div className="space-y-2">
        <Label htmlFor="demo-nome">Nome *</Label>
        <Input
          id="demo-nome"
          placeholder="Seu nome completo"
          value={form.nome}
          onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
          maxLength={100}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="demo-email">Email *</Label>
        <Input
          id="demo-email"
          type="email"
          placeholder="seu@email.com"
          value={form.email}
          onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))}
          maxLength={255}
          required
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="demo-empresa">Empresa *</Label>
        <Input
          id="demo-empresa"
          placeholder="Nome da empresa"
          value={form.empresa}
          onChange={(e) => setForm(prev => ({ ...prev, empresa: e.target.value }))}
          maxLength={150}
          required
        />
      </div>
      <div className="space-y-2">
        <Label>Perfil *</Label>
        <Select value={form.perfil} onValueChange={(v) => setForm(prev => ({ ...prev, perfil: v }))}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione seu perfil" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="investidor_fundo">Investidor / Fundo</SelectItem>
            <SelectItem value="executor_consultoria">Executor / Consultoria</SelectItem>
            <SelectItem value="administracao_publica">Administração Pública / Institucional</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="demo-mensagem">Mensagem (opcional)</Label>
        <Textarea
          id="demo-mensagem"
          placeholder="Conte-nos sobre sua necessidade..."
          value={form.mensagem}
          onChange={(e) => setForm(prev => ({ ...prev, mensagem: e.target.value }))}
          maxLength={1000}
          rows={3}
        />
      </div>
      <Button type="submit" size="lg" className="w-full font-semibold text-base h-12" disabled={loading}>
        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
        Agendar Demonstração
      </Button>
    </form>
  );
};

export default DemoRequestForm;
